import { HttpErrorResponse } from '@angular/common/http';

import { createSelectorFactory, defaultMemoize, MemoizedSelector } from '@ngrx/store';

import type { PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  createHydratedEntityState,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

export const disqualifyReasonIdScope = (disqualifyReasonId: number) =>
  scopeFactory('disqualifyReasonId', disqualifyReasonId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('DisqualifyReasonsAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchDisqualifyReasons: defaultActions.fetchCollection,
        fetchDisqualifyReasonsSuccess: (state, { data }) =>
          pipeState(
            state,
            next =>
              createHydratedEntityState(
                next,
                (<Array<{ id: number; workflowAutomations: WorkflowAutomation[] }>>data).reduce(
                  (acc, disqualifyReasonAutomation) => {
                    const scopeId = disqualifyReasonIdScope(disqualifyReasonAutomation.id);
                    acc[scopeId] = disqualifyReasonAutomation.workflowAutomations;
                    return acc;
                  },
                  <Record<string, WorkflowAutomation[]>>{},
                ),
                adapter,
              ),
            next => adapter.markAsLoaded(next, { scope: null, entities: [] }),
          ),
        fetchDisqualifyReasonsFailed: defaultActions.fetchCollectionFailed,
        createOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationCreatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationUpdatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (
          state,
          payload: { data: number; additionalData: WorkflowAutomationAdditionalData },
        ) => defaultActions.removeOnePessimistic(state, payload),
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
        fetchAutomationTemplates: ngrxEvent<{ id: number }>(),
        fetchAutomationTemplatesSuccess: ngrxEvent<AutomationTemplate[]>(),
        fetchAutomationTemplatesFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const byIds = memoizeSelector(
  (automationIds: number[]): MemoizedSelector<any, WorkflowAutomation[]> =>
    createSelectorFactory(defaultMemoize)(
      ...(automationIds || []).map(automationId => selectors.byId(automationId)),
      (...result: WorkflowAutomation[]) => result.filter(automation => automation),
    ),
);

const DisqualifyReasonsAutomationsSelectors = {
  ...selectors,
  byIds,
};

const DisqualifyReasonsAutomationsActions: PickActions<
  typeof actions,
  | 'fetchDisqualifyReasons'
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
  | 'fetchOptions'
  | 'fetchAutomationTemplates'
> = actions;

export {
  DisqualifyReasonsAutomationsActions,
  namespace as DisqualifyReasonsAutomationsNamespace,
  reducer as DisqualifyReasonsAutomationsReducer,
  DisqualifyReasonsAutomationsSelectors,
};
